import { IResourceComponentsProps, HttpError } from "@pankod/refine-core";

import {
  List,
  Table,
  Tag,
  useTable,
  EditButton,
  Icons,
  FilterDropdown,
  Typography,
  useModalForm,
  Radio,
  useSelect,
  Select,
  Form,
  Input,
  Button,
} from "@pankod/refine-antd";

import { TagsCreate, TagsEdit } from "./";

import { TagTypes } from "interfaces/enums";
import { Tags } from "interfaces";
import { useState } from "react";

const { Title } = Typography;

interface SearchVariables {
  q: string;
}

export const TagsList: React.FC<IResourceComponentsProps> = () => {
  const [searchForm] = Form.useForm();
  const [editingTagId, setEditingTagId] = useState<string | undefined>();
  const { tableProps, searchFormProps, tableQueryResult } = useTable<
    Tags.TagItem,
    HttpError,
    SearchVariables
  >({
    onSearch: (params) => {
      const { q } = params;

      return [
        {
          field: "name",
          operator: "contains",
          value: q || undefined,
        },
      ];
    },
  });

  const { selectProps: superCategorySelectProps } = useSelect({
    resource: "super-category",
    optionLabel: "name",
    optionValue: "id",
  });

  const {
    formProps: tagCreateFormProps,
    modalProps: tagCreateModalProps,
    show: tagCreateModalShow,
  } = useModalForm({
    action: "create",
    submitOnEnter: true,
  });

  const {
    formProps: tagEditFormProps,
    modalProps: tagEditModalProps,
    show: tagEditModalShow,
  } = useModalForm<Tags.TagItem>({
    action: "edit",
    submitOnEnter: true,
  });

  return (
    <>
      <List
        title=""
        createButtonProps={{
          type: "primary",
          size: "large",
          shape: "round",
          icon: <Icons.UploadOutlined />,
          onClick: () => tagCreateModalShow(),
        }}
      >
        <Form
          {...searchFormProps}
          form={searchForm}
          layout="inline"
          style={{ marginBottom: 16 }}
        >
          <Form.Item name="q">
            <Input
              placeholder="Etiket adına göre arama yapınız..."
              prefix={<Icons.SearchOutlined />}
              allowClear
              style={{ width: 300 }}
            />
          </Form.Item>
          <Form.Item>
            <Button htmlType="submit" type="primary">
              Ara
            </Button>
          </Form.Item>
          <Form.Item>
            <Button
              onClick={() => {
                searchForm.resetFields();
                searchForm.submit();
              }}
            >
              Sıfırla
            </Button>
          </Form.Item>
        </Form>

        <Table {...tableProps} scroll={{ x: 1 }} rowKey="id">
          <Table.Column title="Etiket Adı" dataIndex="name" />
          <Table.Column<Tags.TagItem>
            title="Alt Katalog (1. seviye)"
            dataIndex={["superCategory", "subCatalog", "id"]}
            render={(_, record) => (
              <span>
                {record.superCategory?.subCatalog
                  ? record.superCategory.subCatalog.name
                  : "-"}
              </span>
            )}
          />
          <Table.Column<Tags.TagItem>
            title="Süper Kategori (2. seviye)"
            dataIndex={["superCategory", "id"]}
            render={(_, record) => (
              <span>
                {record.superCategory ? record.superCategory.name : "-"}
              </span>
            )}
            filterDropdown={(props) => (
              <FilterDropdown {...props}>
                <Title level={5}>Süper Kategori</Title>
                <Select
                  style={{ minWidth: 200 }}
                  allowClear
                  placeholder="Süper Kategori Seçin"
                  {...superCategorySelectProps}
                />
              </FilterDropdown>
            )}
          />
          <Table.Column<Tags.TagItem>
            title="Süper Kategori Gösterim Sırası"
            dataIndex="superCategoryOrder"
            sorter
            render={(value) => value || "-"}
          />
          <Table.Column<Tags.TagItem>
            title="Tip"
            dataIndex="type"
            render={(_, tag) => (
              <span>
                <Tag>{tag.type}</Tag>
              </span>
            )}
            filterDropdown={(props) => {
              return (
                <FilterDropdown {...props}>
                  <Title level={5}>Etiket Tipi</Title>
                  <Radio.Group
                    options={[
                      { label: "Asset", value: TagTypes.ASSET },
                      { label: "Template", value: TagTypes.TEMPLATE },
                    ]}
                  />
                </FilterDropdown>
              );
            }}
          />
          <Table.Column<Tags.TagItem>
            title="Durum"
            dataIndex="isActive"
            render={(_, record) => {
              if (record.isActive) {
                return <Tag color="success">Aktif</Tag>;
              }

              return <Tag color="red">Pasif</Tag>;
            }}
            filterDropdown={(props) => {
              return (
                <FilterDropdown {...props}>
                  <Title level={5}>Durum</Title>
                  <Radio.Group
                    options={[
                      { label: "Aktif", value: "1" },
                      { label: "Pasif", value: "0" },
                    ]}
                  />
                </FilterDropdown>
              );
            }}
          />
          <Table.Column<Tags.TagItem>
            title="Stok Politikası"
            dataIndex={["superCategories", "hideOutOfStock"]}
            render={(_, record) =>
              record.superCategories?.hideOutOfStock === "true" ? (
                <Tag color="orange">Stoksuz ürünler gizli</Tag>
              ) : (
                <span>-</span>
              )
            }
          />
          <Table.Column<Tags.TagItem>
            title="İşlemler"
            dataIndex="actions"
            render={(_, record) => (
              <EditButton
                size="small"
                onClick={() => {
                  setEditingTagId(record.id);
                  tagEditModalShow(record.id);
                }}
              >
                Düzenle
              </EditButton>
            )}
          />
        </Table>
      </List>
      <TagsCreate
        formProps={tagCreateFormProps}
        modalProps={tagCreateModalProps}
      />
      <TagsEdit
        formProps={tagEditFormProps}
        modalProps={tagEditModalProps}
        // Satırın kopyasını tutmak yerine tablodan türetiyoruz; refetch sonrası
        // düzenlenen etiketin stok politikası her zaman güncel okunur.
        tag={tableProps.dataSource?.find((item) => item.id === editingTagId)}
        onStockPolicySaved={() => tableQueryResult.refetch()}
      />
    </>
  );
};
